/**
 * 테마·배색 appearance 저장소
 * - 기관: appearanceTenant|{tenantCode}
 * - 개인: appearanceUserPrefs|{tenantCode}|{userId}
 * - BE 연동 전까지 localStorage SSOT (payload에 version·savedAt 포함)
 */

import {
  defaultAppearanceSettings,
  DEFAULT_BRAND_MAIN,
} from 'styles/appearanceSlots';
import { softenBrand, mixColors, normalizeHex } from 'styles/brandColorUtils';
import { mergeAppearance } from 'styles/buildThemeFromAppearance';
import { normalizeFontSizeRootPx } from 'styles/erpFonts';
import {
  isBdApiEnabled,
  POC_BUCKET,
  pocParams,
  loadPocDoc,
  upsertPocDoc,
  enqueuePersist,
} from 'services/pocRowPersist';

export const APPEARANCE_TENANT_PREFIX = 'appearanceTenant';
export const APPEARANCE_USER_PREFIX = 'appearanceUserPrefs';
export const APPEARANCE_VERSION = 1;

/** BE 동기화 대기 큐 (Phase3 FE 준비) */
export const APPEARANCE_SYNC_QUEUE_KEY = 'appearanceSyncQueue';

export function appearanceTenantKey(tenantCode) {
  return [APPEARANCE_TENANT_PREFIX, tenantCode || ''].join('|');
}

export function appearanceUserKey(tenantCode, userId) {
  return [APPEARANCE_USER_PREFIX, tenantCode || '', userId || ''].join('|');
}

function safeParse(raw) {
  try {
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} tenantCode
 * @returns {import('styles/appearanceSlots').AppearanceSettings}
 */
export function loadTenantAppearance(tenantCode) {
  const defaults = defaultAppearanceSettings();
  if (!tenantCode) return defaults;
  try {
    const raw = localStorage.getItem(appearanceTenantKey(tenantCode));
    if (!raw) return defaults;
    const data = safeParse(raw);
    if (!data) return defaults;
    return {
      ...defaults,
      ...data,
      slotOverrides:
        data.slotOverrides && typeof data.slotOverrides === 'object' ? data.slotOverrides : {},
      ciMixColors: Array.isArray(data.ciMixColors) ? data.ciMixColors : [],
    };
  } catch {
    return defaults;
  }
}

export async function fetchTenantAppearance(tenantCode) {
  const defaults = defaultAppearanceSettings();
  if (!tenantCode) return defaults;
  if (!isBdApiEnabled()) return loadTenantAppearance(tenantCode);
  const doc = await loadPocDoc({
    bucket: POC_BUCKET.APPEARANCE_TENANT,
    rowUid: tenantCode,
    params: pocParams({ tenantCode }),
  });
  if (!doc) return loadTenantAppearance(tenantCode);
  localStorage.setItem(appearanceTenantKey(tenantCode), JSON.stringify(doc));
  return loadTenantAppearance(tenantCode);
}

export async function fetchPersonalAppearance(tenantCode, userId) {
  if (!tenantCode || !userId) return {};
  if (!isBdApiEnabled()) return loadPersonalAppearance(tenantCode, userId);
  const doc = await loadPocDoc({
    bucket: POC_BUCKET.APPEARANCE_USER,
    rowUid: `${tenantCode}|${userId}`,
    params: pocParams({ tenantCode }),
    userKey: userId,
  });
  if (doc) {
    localStorage.setItem(appearanceUserKey(tenantCode, userId), JSON.stringify(doc));
  }
  return loadPersonalAppearance(tenantCode, userId);
}

/**
 * @param {string} tenantCode
 * @param {import('styles/appearanceSlots').AppearanceSettings} appearance
 */
export function saveTenantAppearance(tenantCode, appearance) {
  if (!tenantCode) return loadTenantAppearance(tenantCode);
  const soft = softenBrand(appearance.mainHex || appearance.sourceHex || DEFAULT_BRAND_MAIN);
  const payload = {
    version: APPEARANCE_VERSION,
    mainHex: soft.brandBase,
    sourceHex: soft.sourceHex,
    softened: soft.softened,
    colorMode: appearance.colorMode === 'dark' ? 'dark' : 'light',
    allowPersonalBrand: !!appearance.allowPersonalBrand,
    fontFamilyId: appearance.fontFamilyId || 'pretendard',
    fontSizeRootPx: normalizeFontSizeRootPx(appearance.fontSizeRootPx),
    allowPersonalFontSize: appearance.allowPersonalFontSize !== false,
    slotOverrides:
      appearance.slotOverrides && typeof appearance.slotOverrides === 'object'
        ? { ...appearance.slotOverrides }
        : {},
    ciMixColors: Array.isArray(appearance.ciMixColors) ? appearance.ciMixColors : [],
    ciMixMode: appearance.ciMixMode === 'subtractive' ? 'subtractive' : 'additive',
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(appearanceTenantKey(tenantCode), JSON.stringify(payload));
  enqueueAppearanceSync({ scope: 'tenant', tenantCode, payload });
  if (isBdApiEnabled()) {
    enqueuePersist(`appearance.tenant|${tenantCode}`, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.APPEARANCE_TENANT,
        rowUid: tenantCode,
        payload,
        params: pocParams({ tenantCode }),
      })
    );
  }
  return loadTenantAppearance(tenantCode);
}

/**
 * @param {string} tenantCode
 * @param {string} userId
 */
export function loadPersonalAppearance(tenantCode, userId) {
  if (!tenantCode || !userId) return {};
  try {
    const raw = localStorage.getItem(appearanceUserKey(tenantCode, userId));
    if (!raw) return {};
    const data = safeParse(raw);
    return data || {};
  } catch {
    return {};
  }
}

/**
 * @param {string} tenantCode
 * @param {string} userId
 * @param {Partial<import('styles/appearanceSlots').AppearanceSettings>} personal
 */
export function savePersonalAppearance(tenantCode, userId, personal) {
  if (!tenantCode || !userId) return {};
  const payload = {
    version: APPEARANCE_VERSION,
    colorMode: personal.colorMode === 'dark' || personal.colorMode === 'light' ? personal.colorMode : undefined,
    mainHex: personal.mainHex ? normalizeHex(personal.mainHex) || personal.mainHex : undefined,
    sourceHex: personal.sourceHex,
    softened: personal.softened,
    fontFamilyId: personal.fontFamilyId || undefined,
    fontSizeRootPx:
      personal.fontSizeRootPx != null ? normalizeFontSizeRootPx(personal.fontSizeRootPx) : undefined,
    slotOverrides:
      personal.slotOverrides && typeof personal.slotOverrides === 'object'
        ? { ...personal.slotOverrides }
        : undefined,
    savedAt: new Date().toISOString(),
  };
  // undefined 키 제거
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });
  localStorage.setItem(appearanceUserKey(tenantCode, userId), JSON.stringify(payload));
  enqueueAppearanceSync({ scope: 'personal', tenantCode, userId, payload });
  if (isBdApiEnabled()) {
    enqueuePersist(`appearance.user|${tenantCode}|${userId}`, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.APPEARANCE_USER,
        rowUid: `${tenantCode}|${userId}`,
        payload,
        params: pocParams({ tenantCode }),
        userKey: userId,
      })
    );
  }
  return loadPersonalAppearance(tenantCode, userId);
}

/**
 * 기관 + 개인 머지 결과
 */
export function resolveAppearance(tenantCode, userId) {
  const tenant = loadTenantAppearance(tenantCode);
  const personal = loadPersonalAppearance(tenantCode, userId);
  return mergeAppearance(tenant, personal);
}

/**
 * 기관 생성 시 CI 메인색으로 appearance 초기화/갱신 (기존 슬롯·플래그 유지)
 * @param {string} tenantCode
 * @param {string} mainHex
 * @param {Partial<import('styles/appearanceSlots').AppearanceSettings>} [extra]
 */
export function initTenantAppearanceFromCi(tenantCode, mainHex, extra = {}) {
  const soft = softenBrand(mainHex || DEFAULT_BRAND_MAIN);
  const prev = loadTenantAppearance(tenantCode);
  return saveTenantAppearance(tenantCode, {
    ...prev,
    ...extra,
    mainHex: soft.brandBase,
    sourceHex: soft.sourceHex,
    softened: soft.softened,
  });
}

/**
 * CI 다중색 혼합 → soft main
 */
export function applyCiMixToAppearance(appearance) {
  const colors = appearance.ciMixColors || [];
  if (!colors.length) return appearance;
  const mixed = mixColors(colors, appearance.ciMixMode || 'additive');
  const soft = softenBrand(mixed);
  return {
    ...appearance,
    sourceHex: soft.sourceHex,
    mainHex: soft.brandBase,
    softened: soft.softened,
  };
}

/**
 * BE 미연동 시 동기화 큐에 적재 (추후 flushAppearanceSyncQueue)
 */
function enqueueAppearanceSync(entry) {
  try {
    const raw = localStorage.getItem(APPEARANCE_SYNC_QUEUE_KEY);
    const queue = raw ? safeParse(raw) : [];
    const list = Array.isArray(queue) ? queue : [];
    list.push({ ...entry, queuedAt: new Date().toISOString() });
    // 최근 50건만 유지
    localStorage.setItem(APPEARANCE_SYNC_QUEUE_KEY, JSON.stringify(list.slice(-50)));
  } catch {
    /* ignore */
  }
}

/**
 * BE API 연동 시 호출할 flush 스텁
 * @returns {{ pending: number, items: unknown[] }}
 */
export function peekAppearanceSyncQueue() {
  try {
    const raw = localStorage.getItem(APPEARANCE_SYNC_QUEUE_KEY);
    const queue = raw ? safeParse(raw) : [];
    const items = Array.isArray(queue) ? queue : [];
    return { pending: items.length, items };
  } catch {
    return { pending: 0, items: [] };
  }
}

export function clearAppearanceSyncQueue() {
  localStorage.removeItem(APPEARANCE_SYNC_QUEUE_KEY);
}
